/**
 * DV-TEMP-003: Error Notice Components
 *
 * UI components for displaying API errors with actionable remediation hints.
 * Consistent error messaging across Demo Viewer pages.
 */

import { AlertTriangle, AlertCircle, RefreshCw, ExternalLink, ArrowLeft } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { DemoError } from '@/lib/demo-error-handler';

export interface ErrorNoticeProps {
  error: DemoError;
  onRetry?: () => void;
  onBack?: () => void;
  className?: string;
  'data-testid'?: string;
}

// Base error notice component
function BaseErrorNotice({
  error,
  onRetry,
  onBack,
  className,
  'data-testid': testId = 'error-notice',
  children
}: ErrorNoticeProps & { children: React.ReactNode }) {
  const canRetry = error.retryable && onRetry;

  return (
    <div
      className={cn("rounded-lg border p-4", className)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: error.type === 'server_error' ? 'var(--warning)' : 'var(--danger)'
      }}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {error.type === 'server_error' ? (
            <AlertTriangle className="w-5 h-5" style={{ color: 'var(--warning)' }} />
          ) : (
            <AlertCircle className="w-5 h-5" style={{ color: 'var(--danger)' }} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3
            className="text-sm font-medium mb-2"
            style={{ color: 'var(--fg-primary)' }}
          >
            {error.userMessage}
          </h3>

          {children}

          {/* Remediation Steps */}
          <div className="mt-3">
            <p
              className="text-xs font-medium mb-1"
              style={{ color: 'var(--fg-secondary)' }}
            >
              What you can try:
            </p>
            <ul
              className="text-xs space-y-1"
              style={{ color: 'var(--fg-muted)' }}
            >
              {error.remediation.map((step, index) => (
                <li key={index}>• {step}</li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          {(canRetry || onBack) && (
            <div className="flex items-center gap-2 mt-4">
              {canRetry && (
                <button
                  onClick={onRetry}
                  className="flex items-center gap-1 px-3 py-1.5 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--fg-primary)',
                    ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                    ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-elevated)';
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  data-testid={`${testId}-retry`}
                >
                  <RefreshCw className="w-3 h-3" />
                  Try Again
                </button>
              )}

              {onBack && (
                <button
                  onClick={onBack}
                  className="flex items-center gap-1 px-3 py-1.5 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--fg-primary)',
                    ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                    ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-elevated)';
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  data-testid={`${testId}-back`}
                >
                  <ArrowLeft className="w-3 h-3" />
                  Go Back
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Permission denied notice (401/403)
export function PermissionNotice(props: ErrorNoticeProps) {
  return (
    <BaseErrorNotice {...props} data-testid={props['data-testid'] || 'permission-notice'}>
      <div
        className="text-xs mb-2"
        style={{ color: 'var(--fg-muted)' }}
      >
        {props.error.status === 401
          ? 'Your session may have expired or you need to log in.'
          : 'This execution may belong to another user or require admin access.'
        }
      </div>

      {props.error.correlationId && (
        <div
          className="text-xs font-mono mt-2 p-2 rounded border"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-muted)'
          }}
        >
          Reference ID: {props.error.correlationId}
        </div>
      )}
    </BaseErrorNotice>
  );
}

// Not found notice (404)
export function NotFoundNotice(props: ErrorNoticeProps & {
  executionId?: string;
  stage?: string;
  onExecutionSelect?: () => void;
  onStageSelect?: () => void;
}) {
  const { executionId, stage, onExecutionSelect, onStageSelect } = props;

  return (
    <BaseErrorNotice {...props} data-testid={props['data-testid'] || 'not-found-notice'}>
      <div
        className="text-xs mb-2"
        style={{ color: 'var(--fg-muted)' }}
      >
        {executionId && stage
          ? `Execution "${executionId}" or stage "${stage}" not found.`
          : executionId
            ? `Execution "${executionId}" not found.`
            : 'The requested resource could not be found.'
        }
      </div>

      {/* Debug Information */}
      {props.error.details && (
        <div
          className="text-xs mb-2 p-2 rounded border font-mono"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-muted)'
          }}
        >
          <div className="font-bold mb-1 text-red-600">🐛 Debug Info:</div>
          {props.error.details.url && (
            <div className="break-all">
              <strong>URL:</strong> {props.error.details.url}
            </div>
          )}
          {props.error.details.method && (
            <div>
              <strong>Method:</strong> {props.error.details.method}
            </div>
          )}
          {props.error.details.params && (
            <div>
              <strong>Params:</strong> {JSON.stringify(props.error.details.params)}
            </div>
          )}
          {props.error.details.requestId && (
            <div>
              <strong>Request ID:</strong> {props.error.details.requestId}
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      {(onExecutionSelect || onStageSelect) && (
        <div className="flex items-center gap-2 mb-3">
          {onExecutionSelect && (
            <button
              onClick={onExecutionSelect}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--accent)',
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid="not-found-select-execution"
            >
              <ExternalLink className="w-3 h-3" />
              Choose Execution
            </button>
          )}

          {onStageSelect && (
            <button
              onClick={onStageSelect}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--accent)',
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid="not-found-select-stage"
            >
              <ExternalLink className="w-3 h-3" />
              Choose Stage
            </button>
          )}
        </div>
      )}

      {props.error.correlationId && (
        <div
          className="text-xs font-mono mt-2 p-2 rounded border"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-muted)'
          }}
        >
          Reference ID: {props.error.correlationId}
        </div>
      )}
    </BaseErrorNotice>
  );
}

// Server error notice (5xx) with retry countdown
export function ServerErrorNotice(props: ErrorNoticeProps) {
  return (
    <BaseErrorNotice {...props} data-testid={props['data-testid'] || 'server-error-notice'}>
      <div
        className="text-xs mb-2"
        style={{ color: 'var(--fg-muted)' }}
      >
        The server is experiencing temporary issues. This usually resolves quickly.
      </div>

      {props.error.canRetryIn && (
        <div
          className="text-xs mb-2 flex items-center gap-1"
          style={{ color: 'var(--warning)' }}
        >
          <RefreshCw className="w-3 h-3" />
          Auto-retry in {Math.ceil(props.error.canRetryIn / 1000)}s
        </div>
      )}

      {props.error.correlationId && (
        <div
          className="text-xs font-mono mt-2 p-2 rounded border"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-muted)'
          }}
        >
          Reference ID: {props.error.correlationId}
        </div>
      )}
    </BaseErrorNotice>
  );
}

// Conflict notice for 409 errors - concurrent modifications
export function ConflictNotice(props: ErrorNoticeProps) {
  return (
    <BaseErrorNotice {...props} data-testid={props['data-testid'] || 'conflict-notice'}>
      <div
        className="text-xs mb-2"
        style={{ color: 'var(--fg-muted)' }}
      >
        The item was modified by another user while you were editing it. Your changes could not be applied.
      </div>

      <div
        className="text-xs mb-2 p-2 rounded border"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--warning)',
          color: 'var(--warning)'
        }}
      >
        ⚠️ The data has been refreshed to show the current state. Please review and try again.
      </div>

      {props.error.correlationId && (
        <div
          className="text-xs font-mono mt-2 p-2 rounded border"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-muted)'
          }}
        >
          Reference ID: {props.error.correlationId}
        </div>
      )}
    </BaseErrorNotice>
  );
}

// Inline error banner for transient errors
export function ErrorBanner({
  error,
  onDismiss,
  onRetry,
  className,
  'data-testid': testId = 'error-banner'
}: {
  error: DemoError;
  onDismiss?: () => void;
  onRetry?: () => void;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <div
      className={cn("rounded border p-3 flex items-center gap-3", className)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: error.type === 'server_error' ? 'var(--warning)' : 'var(--danger)',
        opacity: 0.9
      }}
      data-testid={testId}
    >
      <AlertTriangle
        className="w-4 h-4 flex-shrink-0"
        style={{ color: error.type === 'server_error' ? 'var(--warning)' : 'var(--danger)' }}
      />

      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-medium"
          style={{ color: 'var(--fg-primary)' }}
        >
          {error.userMessage}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {error.retryable && onRetry && (
          <button
            onClick={onRetry}
            className="text-xs px-2 py-1 rounded border transition-colors focus:outline-none focus:ring-1"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-primary)',
              ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
              ['--focus-ring' as any]: '0 0 0 1px var(--border-focus)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = 'var(--focus-ring)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
            data-testid={`${testId}-retry`}
          >
            Retry
          </button>
        )}

        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-xs px-2 py-1 rounded transition-colors focus:outline-none focus:ring-1"
            style={{
              color: 'var(--fg-muted)',
              ['--hover-color' as any]: 'var(--fg-primary)',
              ['--focus-ring' as any]: '0 0 0 1px var(--border-focus)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--hover-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--fg-muted)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = 'var(--focus-ring)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
            data-testid={`${testId}-dismiss`}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}